/**
 * Emotion/Reaction icon configuration for the widget
 * This file defines all 100 available emotion reactions that can be used in the widget
 * Extended from 6 to 100 reactions to support various niches (tech, food, education, business, etc.)
 */

/**
 * Emotion category types
 */
export type EmotionCategory = 'general' | 'emotions' | 'tech' | 'food' | 'celebration' | 'gestures' | 'symbols' | 'education' | 'business';

/**
 * Emotion icon type definition
 */
export interface EmotionIcon {
  /** Unique identifier for the emotion type */
  type: string;
  /** Display label for the emotion */
  label: string;
  /** Emoji character to display */
  emoji: string;
  /** Display order (lower numbers appear first) */
  order: number;
  /** Category for filtering */
  category: EmotionCategory;
  /** Whether this emotion is enabled by default */
  defaultEnabled: boolean;
}

/**
 * All available emotion icons (100 reactions)
 * These correspond to the ReactionType enum in the backend
 * IDs 1-6 are preserved for backward compatibility
 */
export const AVAILABLE_EMOTIONS: EmotionIcon[] = [
  // 1-6: Original Reactions (Backward Compatible)
  { type: 'like', label: 'Like', emoji: '👍', order: 1, category: 'general', defaultEnabled: true },
  { type: 'dislike', label: 'Dislike', emoji: '👎', order: 2, category: 'general', defaultEnabled: false },
  { type: 'love', label: 'Love', emoji: '❤️', order: 3, category: 'general', defaultEnabled: true },
  { type: 'laugh', label: 'Laugh', emoji: '😄', order: 4, category: 'general', defaultEnabled: true },
  { type: 'sad', label: 'Sad', emoji: '😢', order: 5, category: 'emotions', defaultEnabled: false },
  { type: 'angry', label: 'Angry', emoji: '😠', order: 6, category: 'emotions', defaultEnabled: false },

  // 7-26: General/Universal
  { type: 'happy', label: 'Happy', emoji: '😊', order: 7, category: 'general', defaultEnabled: true },
  { type: 'surprised', label: 'Surprised', emoji: '😮', order: 8, category: 'general', defaultEnabled: false },
  { type: 'thinking', label: 'Thinking', emoji: '🤔', order: 9, category: 'general', defaultEnabled: false },
  { type: 'heart_eyes', label: 'Heart Eyes', emoji: '😍', order: 10, category: 'general', defaultEnabled: false },
  { type: 'clap', label: 'Clap', emoji: '👏', order: 11, category: 'general', defaultEnabled: false },
  { type: 'pray', label: 'Thank You', emoji: '🙏', order: 12, category: 'general', defaultEnabled: false },
  { type: 'fire', label: 'Fire', emoji: '🔥', order: 13, category: 'general', defaultEnabled: false },
  { type: 'sparkles', label: 'Sparkles', emoji: '✨', order: 14, category: 'general', defaultEnabled: false },
  { type: 'hundred', label: 'Perfect', emoji: '💯', order: 15, category: 'general', defaultEnabled: false },
  { type: 'party', label: 'Party', emoji: '🎉', order: 16, category: 'general', defaultEnabled: false },
  { type: 'eyes', label: 'Eyes', emoji: '👀', order: 17, category: 'general', defaultEnabled: false },
  { type: 'muscle', label: 'Strong', emoji: '💪', order: 18, category: 'general', defaultEnabled: false },
  { type: 'hugging', label: 'Hug', emoji: '🤗', order: 19, category: 'general', defaultEnabled: false },
  { type: 'cool', label: 'Cool', emoji: '�', order: 20, category: 'general', defaultEnabled: false },
  { type: 'raised_hands', label: 'Praise', emoji: '🙌', order: 21, category: 'general', defaultEnabled: false },
  { type: 'star', label: 'Star', emoji: '⭐', order: 22, category: 'general', defaultEnabled: false },
  { type: 'speech_bubble', label: 'Comment', emoji: '💬', order: 23, category: 'general', defaultEnabled: false },
  { type: 'pin', label: 'Pin', emoji: '📌', order: 24, category: 'general', defaultEnabled: false },
  { type: 'check_mark', label: 'Correct', emoji: '✅', order: 25, category: 'general', defaultEnabled: false },
  { type: 'question', label: 'Question', emoji: '❓', order: 26, category: 'general', defaultEnabled: false },

  // 27-41: Emotions
  { type: 'joy', label: 'Joy', emoji: '😂', order: 27, category: 'emotions', defaultEnabled: false },
  { type: 'smiling_hearts', label: 'Adore', emoji: '🥰', order: 28, category: 'emotions', defaultEnabled: false },
  { type: 'crying', label: 'Crying', emoji: '😭', order: 29, category: 'emotions', defaultEnabled: false },
  { type: 'scream', label: 'Shocked', emoji: '😱', order: 30, category: 'emotions', defaultEnabled: false },
  { type: 'pleading', label: 'Pleading', emoji: '🥺', order: 31, category: 'emotions', defaultEnabled: false },
  { type: 'triumph', label: 'Determined', emoji: '😤', order: 32, category: 'emotions', defaultEnabled: false },
  { type: 'exploding_head', label: 'Mind Blown', emoji: '🤯', order: 33, category: 'emotions', defaultEnabled: false },
  { type: 'grimace', label: 'Awkward', emoji: '😬', order: 34, category: 'emotions', defaultEnabled: false },
  { type: 'partying', label: 'Partying', emoji: '🥳', order: 35, category: 'emotions', defaultEnabled: false },
  { type: 'sleeping', label: 'Boring', emoji: '😴', order: 36, category: 'emotions', defaultEnabled: false },
  { type: 'star_struck', label: 'Star Struck', emoji: '🤩', order: 37, category: 'emotions', defaultEnabled: false },
  { type: 'angel', label: 'Innocent', emoji: '�', order: 38, category: 'emotions', defaultEnabled: false },
  { type: 'zany', label: 'Crazy', emoji: '🤪', order: 39, category: 'emotions', defaultEnabled: false },
  { type: 'smirk', label: 'Smirk', emoji: '😏', order: 40, category: 'emotions', defaultEnabled: false },
  { type: 'woozy', label: 'Confused', emoji: '🥴', order: 41, category: 'emotions', defaultEnabled: false },

  // 42-51: Tech/Developer
  { type: 'lightbulb', label: 'Insightful', emoji: '💡', order: 42, category: 'tech', defaultEnabled: false },
  { type: 'rocket', label: 'Innovative', emoji: '🚀', order: 43, category: 'tech', defaultEnabled: false },
  { type: 'bug', label: 'Bug', emoji: '🐛', order: 44, category: 'tech', defaultEnabled: false },
  { type: 'zap', label: 'Fast', emoji: '⚡', order: 45, category: 'tech', defaultEnabled: false },
  { type: 'wrench', label: 'Fix', emoji: '🔧', order: 46, category: 'tech', defaultEnabled: false },
  { type: 'laptop', label: 'Code', emoji: '💻', order: 47, category: 'tech', defaultEnabled: false },
  { type: 'target', label: 'Accurate', emoji: '🎯', order: 48, category: 'tech', defaultEnabled: false },
  { type: 'lock', label: 'Security', emoji: '🔒', order: 49, category: 'tech', defaultEnabled: false },
  { type: 'chart', label: 'Data', emoji: '📊', order: 50, category: 'tech', defaultEnabled: false },
  { type: 'robot', label: 'AI', emoji: '🤖', order: 51, category: 'tech', defaultEnabled: false },

  // 52-61: Food/Cooking
  { type: 'yum', label: 'Yummy', emoji: '😋', order: 52, category: 'food', defaultEnabled: false },
  { type: 'drooling', label: 'Hungry', emoji: '🤤', order: 53, category: 'food', defaultEnabled: false },
  { type: 'chef', label: 'Will Try', emoji: '👨‍🍳', order: 54, category: 'food', defaultEnabled: false },
  { type: 'pizza', label: 'Pizza', emoji: '🍕', order: 55, category: 'food', defaultEnabled: false },
  { type: 'cake', label: 'Dessert', emoji: '🍰', order: 56, category: 'food', defaultEnabled: false },
  { type: 'coffee', label: 'Coffee', emoji: '☕', order: 57, category: 'food', defaultEnabled: false },
  { type: 'salad', label: 'Healthy', emoji: '🥗', order: 58, category: 'food', defaultEnabled: false },
  { type: 'taco', label: 'Spicy', emoji: '🌮', order: 59, category: 'food', defaultEnabled: false },
  { type: 'burger', label: 'Comfort Food', emoji: '🍔', order: 60, category: 'food', defaultEnabled: false },
  { type: 'wine', label: 'Fancy', emoji: '🍷', order: 61, category: 'food', defaultEnabled: false },

  // 62-71: Celebration
  { type: 'trophy', label: 'Winner', emoji: '🏆', order: 62, category: 'celebration', defaultEnabled: false },
  { type: 'confetti', label: 'Congrats', emoji: '🎊', order: 63, category: 'celebration', defaultEnabled: false },
  { type: 'gold_medal', label: 'First Place', emoji: '🥇', order: 64, category: 'celebration', defaultEnabled: false },
  { type: 'birthday', label: 'Birthday', emoji: '🎂', order: 65, category: 'celebration', defaultEnabled: false },
  { type: 'gift', label: 'Gift', emoji: '🎁', order: 66, category: 'celebration', defaultEnabled: false },
  { type: 'balloon', label: 'Festive', emoji: '🎈', order: 67, category: 'celebration', defaultEnabled: false },
  { type: 'champagne', label: 'Cheers', emoji: '🍾', order: 68, category: 'celebration', defaultEnabled: false },
  { type: 'glowing_star', label: 'Achievement', emoji: '🌟', order: 69, category: 'celebration', defaultEnabled: false },
  { type: 'bullseye', label: 'Goal Reached', emoji: '🎯', order: 70, category: 'celebration', defaultEnabled: false },
  { type: 'crown', label: 'Royalty', emoji: '�', order: 71, category: 'celebration', defaultEnabled: false },

  // 72-81: Gestures
  { type: 'fist', label: 'Solidarity', emoji: '✊', order: 72, category: 'gestures', defaultEnabled: false },
  { type: 'handshake', label: 'Agreement', emoji: '🤝', order: 73, category: 'gestures', defaultEnabled: false },
  { type: 'wave', label: 'Hello', emoji: '👋', order: 74, category: 'gestures', defaultEnabled: false },
  { type: 'peace', label: 'Peace', emoji: '✌️', order: 75, category: 'gestures', defaultEnabled: false },
  { type: 'crossed_fingers', label: 'Hope', emoji: '🤞', order: 76, category: 'gestures', defaultEnabled: false },
  { type: 'fist_bump', label: 'Respect', emoji: '👊', order: 77, category: 'gestures', defaultEnabled: false },
  { type: 'hand', label: 'Stop', emoji: '🖐️', order: 78, category: 'gestures', defaultEnabled: false },
  { type: 'ok_hand', label: 'OK', emoji: '👌', order: 79, category: 'gestures', defaultEnabled: false },
  { type: 'no_gesture', label: 'Disagree', emoji: '🙅', order: 80, category: 'gestures', defaultEnabled: false },
  { type: 'yes_gesture', label: 'Agree', emoji: '🙆', order: 81, category: 'gestures', defaultEnabled: false },

  // 82-92: Symbols
  { type: 'warning', label: 'Warning', emoji: '⚠️', order: 82, category: 'symbols', defaultEnabled: false },
  { type: 'x_mark', label: 'Wrong', emoji: '❌', order: 83, category: 'symbols', defaultEnabled: false },
  { type: 'recycle', label: 'Reuse', emoji: '♻️', order: 84, category: 'symbols', defaultEnabled: false },
  { type: 'red_circle', label: 'Important', emoji: '🔴', order: 85, category: 'symbols', defaultEnabled: false },
  { type: 'money_bag', label: 'Money', emoji: '💰', order: 86, category: 'symbols', defaultEnabled: false },
  { type: 'trending_up', label: 'Growth', emoji: '📈', order: 87, category: 'symbols', defaultEnabled: false },
  { type: 'trending_down', label: 'Decline', emoji: '📉', order: 88, category: 'symbols', defaultEnabled: false },
  { type: 'palette', label: 'Creative', emoji: '🎨', order: 89, category: 'symbols', defaultEnabled: false },
  { type: 'bell', label: 'Notify', emoji: '🔔', order: 90, category: 'symbols', defaultEnabled: false },
  { type: 'gem', label: 'Valuable', emoji: '💎', order: 91, category: 'symbols', defaultEnabled: false },
  { type: 'alarm', label: 'Urgent', emoji: '⏰', order: 92, category: 'symbols', defaultEnabled: false },

  // 93-97: Education
  { type: 'books', label: 'Learning', emoji: '�', order: 93, category: 'education', defaultEnabled: false },
  { type: 'graduation', label: 'Graduate', emoji: '🎓', order: 94, category: 'education', defaultEnabled: false },
  { type: 'pencil', label: 'Note', emoji: '✏️', order: 95, category: 'education', defaultEnabled: false },
  { type: 'brain', label: 'Smart', emoji: '🧠', order: 96, category: 'education', defaultEnabled: false },
  { type: 'microscope', label: 'Science', emoji: '🔬', order: 97, category: 'education', defaultEnabled: false },

  // 98-100: Business
  { type: 'briefcase', label: 'Professional', emoji: '💼', order: 98, category: 'business', defaultEnabled: false },
  { type: 'phone', label: 'Mobile', emoji: '📱', order: 99, category: 'business', defaultEnabled: false },
  { type: 'earth', label: 'Global', emoji: '🌍', order: 100, category: 'business', defaultEnabled: false },
];

/**
 * Get emotion configuration by type
 * @param type - The emotion type identifier
 * @returns EmotionIcon if found, undefined otherwise
 */
export function getEmotionByType(type: string): EmotionIcon | undefined {
  return AVAILABLE_EMOTIONS.find(emotion => emotion.type === type);
}

/**
 * Get all enabled emotions by default
 * @returns Array of emotions with defaultEnabled = true
 */
export function getDefaultEnabledEmotions(): EmotionIcon[] {
  return AVAILABLE_EMOTIONS.filter(emotion => emotion.defaultEnabled).sort((a, b) => a.order - b.order);
}

/**
 * Get emotions by list of types
 * @param types - Array of emotion type identifiers
 * @returns Array of matching EmotionIcon objects, sorted by order
 */
export function getEmotionsByTypes(types: string[]): EmotionIcon[] {
  const emotions = types
    .map(type => AVAILABLE_EMOTIONS.find(emotion => emotion.type === type))
    .filter((emotion): emotion is EmotionIcon => emotion !== undefined);
  
  return emotions.sort((a, b) => a.order - b.order);
}

/**
 * Get emotions by category
 * @param category - The category to filter by
 * @returns Array of EmotionIcon objects in the specified category, sorted by order
 */
export function getEmotionsByCategory(category: EmotionCategory): EmotionIcon[] {
  return AVAILABLE_EMOTIONS
    .filter(emotion => emotion.category === category)
    .sort((a, b) => a.order - b.order);
}

/**
 * Get emotions by multiple categories
 * @param categories - Array of categories to filter by
 * @returns Array of EmotionIcon objects in the specified categories, sorted by order
 */
export function getEmotionsByCategories(categories: EmotionCategory[]): EmotionIcon[] {
  return AVAILABLE_EMOTIONS
    .filter(emotion => categories.includes(emotion.category))
    .sort((a, b) => a.order - b.order);
}

/**
 * Get all available categories
 * @returns Array of unique category names
 */
export function getAllCategories(): EmotionCategory[] {
  return Array.from(new Set(AVAILABLE_EMOTIONS.map(emotion => emotion.category)));
}

/**
 * Validate that emotion types are valid
 * @param types - Array of emotion type identifiers to validate
 * @returns true if all types are valid, false otherwise
 */
export function validateEmotionTypes(types: string[]): boolean {
  return types.every(type => AVAILABLE_EMOTIONS.some(emotion => emotion.type === type));
}

/**
 * Validate that categories are valid
 * @param categories - Array of category names to validate
 * @returns true if all categories are valid, false otherwise
 */
export function validateCategories(categories: string[]): boolean {
  const validCategories = getAllCategories();
  return categories.every(cat => validCategories.includes(cat as EmotionCategory));
}
